/**
 * Per-tab memory of "I started this report and want to land on it".
 *
 * The store that holds this is in-memory, so a refresh mid-generation dropped
 * both the progress dialog and the handover: the dashboard only re-opens the
 * dialog by itself when the company has *nothing* finished to show instead
 * (`buildingReportIdOf`), which is exactly not the case for anyone who already
 * has a report. They came back to their old dashboard with a build running and
 * nothing to take them to it when it landed.
 *
 * `sessionStorage`, not `localStorage`: this is a per-tab intent. It should
 * survive a reload and die with the tab, so a report started days ago can never
 * yank a fresh session somewhere the user didn't ask to go.
 */

// Versioned so a future shape change can't misparse an old tab's value.
const KEY = "awaitedReport:v1";

/**
 * How long an unresolved intent stays honoured.
 *
 * Normally it is cleared the moment the report is seen to finish. This only
 * covers the case where that never happens — a report that dies without ever
 * reaching a terminal status, or one that ages out of the recent-reports window
 * the progress poll reads — so the tab doesn't carry a pending navigation
 * forever.
 */
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export interface AwaitedReport {
  reportId: number;
  /** Was the progress dialog on screen? A deliberate dismissal survives a reload. */
  dialogOpen: boolean;
  /** When the report was claimed — the minimum-dwell pacing continues across a reload. */
  at: number | null;
}

export const readAwaitedReport = (): AwaitedReport | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AwaitedReport & { at?: number };
    if (typeof parsed?.reportId !== "number") return null;
    if (typeof parsed.at === "number" && Date.now() - parsed.at > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return {
      reportId: parsed.reportId,
      dialogOpen: !!parsed.dialogOpen,
      at: typeof parsed.at === "number" ? parsed.at : null,
    };
  } catch {
    // Private-mode / quota / malformed value. A missing intent costs the user
    // one manual click; a throw here would take the whole store's init with it.
    return null;
  }
};

export const writeAwaitedReport = (
  reportId: number,
  dialogOpen: boolean,
  // Claim time. Callers recording a later dialog open/close pass the original
  // claim through, so dismissing the dialog doesn't reset the pacing clock.
  at: number = Date.now(),
): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ reportId, dialogOpen, at }));
  } catch {
    /* non-fatal — see above */
  }
};

export const clearStoredAwaitedReport = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* non-fatal — see above */
  }
};
